import {
  EARCHIVE_REQUEST_STATUS_LABELS,
  type EarchiveRequest,
  earchiveRequestStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  useToast,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Receipt } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { useCompanyStations } from "../../../features/accounting/shared.js";
import { ReasonDialog } from "../../../features/counterparties/shared.js";
import {
  EarchiveRequestBadge,
  InvoicingPage,
  useInvoicingCrumb,
  useInvoicingPermissions,
  useRefreshInvoicing,
} from "../../../features/invoicing/shared.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const searchSchema = z.object({
  ...companySearchShape,
  status: earchiveRequestStatusSchema.optional().catch(undefined),
  stationId: z.string().uuid().optional().catch(undefined),
});
type RequestSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/faturalar/e-arsiv-talepleri")({
  validateSearch: (search) => searchSchema.parse(search),
  component: EarchiveRequestsPage,
});

function EarchiveRequestsPage() {
  const { t } = useTranslation();
  const crumb = useInvoicingCrumb("/faturalar");
  const search = Route.useSearch();
  return (
    <InvoicingPage
      title={t("menu.earchiveRequests")}
      description={t("invoicing.requests.description")}
      breadcrumb={[crumb, { label: t("menu.earchiveRequests") }]}
    >
      {({ companyId }) => <RequestList companyId={companyId} search={search} />}
    </InvoicingPage>
  );
}

function RequestList({ companyId, search }: { companyId: string; search: RequestSearch }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate({ from: Route.fullPath });
  const shape = useTenantShape();
  const stations = useCompanyStations(companyId);
  const refresh = useRefreshInvoicing();
  const permissions = useInvoicingPermissions();
  const [rejecting, setRejecting] = useState<EarchiveRequest | null>(null);

  const setFilters = (patch: Partial<RequestSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  const query = {
    companyId,
    limit: 25,
    ...(search.status ? { status: search.status } : {}),
    ...(search.stationId ? { stationId: search.stationId } : {}),
  };
  const pages = useCursorPages(JSON.stringify(query));
  const list = useQuery({
    queryKey: queryKeys.invoicing(companyId, "earchive-requests", {
      ...query,
      cursor: pages.cursor ?? null,
    }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/earchive-requests", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
  });

  const columns: DataTableColumn<EarchiveRequest>[] = [
    {
      id: "station",
      header: t("invoicing.fields.station"),
      cell: (row) => row.stationName ?? "—",
      exportValue: (row) => row.stationName ?? "",
    },
    {
      id: "receipt",
      header: t("invoicing.requests.receipt"),
      cell: (row) => (
        <span className="grid">
          <span className="tabular">{formatDate(row.receiptDate)}</span>
          <span className="font-mono text-xs text-muted-foreground">
            {[row.fiscalDeviceSerial, row.zNo, row.receiptNo].filter((part) => part !== null).join(" · ") ||
              "—"}
          </span>
        </span>
      ),
      exportValue: (row) => `${row.receiptDate} ${row.fiscalDeviceSerial ?? ""}`,
    },
    {
      id: "buyer",
      header: t("invoicing.requests.buyer"),
      cell: (row) => (
        <span className="grid">
          {row.buyerName}
          <span className="font-mono text-xs text-muted-foreground">{row.buyerTaxId}</span>
        </span>
      ),
      exportValue: (row) => `${row.buyerName} (${row.buyerTaxId})`,
    },
    {
      id: "amount",
      header: t("invoicing.fields.payableAmount"),
      align: "right",
      cell: (row) => <MoneyText value={row.amount} />,
      exportValue: (row) => row.amount,
    },
    {
      id: "status",
      header: t("common.status"),
      cell: (row) => <EarchiveRequestBadge status={row.status} />,
      exportValue: (row) => EARCHIVE_REQUEST_STATUS_LABELS[row.status],
    },
    {
      id: "actions",
      header: t("common.actions"),
      align: "right",
      hideable: false,
      cell: (row) =>
        row.status !== "PENDING" ? null : (
          <span className="flex justify-end gap-1">
            {permissions.canManageSales ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void navigate({ to: "/faturalar/yeni", search: { talep: row.id } })}
              >
                {t("invoicing.requests.convert")}
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setRejecting(row)}>
              {t("invoicing.requests.reject")}
            </Button>
          </span>
        ),
    },
  ];

  return (
    <>
      <DataTable
        caption={t("menu.earchiveRequests")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Receipt />}
            title={t("invoicing.requests.emptyTitle")}
            description={t("invoicing.requests.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("common.status")} className="w-40">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) =>
                    setFilters({
                      status:
                        event.target.value === ""
                          ? undefined
                          : earchiveRequestStatusSchema.parse(event.target.value),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {earchiveRequestStatusSchema.options.map((status) => (
                    <option key={status} value={status}>
                      {EARCHIVE_REQUEST_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            {shape.isSingleStation ? null : (
              <FormField label={t("invoicing.fields.station")} className="w-48">
                {(control) => (
                  <Select
                    {...control}
                    value={search.stationId ?? ""}
                    onChange={(event) => setFilters({ stationId: event.target.value || undefined })}
                  >
                    <option value="">{t("common.all")}</option>
                    {(stations.data ?? []).map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            )}
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
      <ReasonDialog
        open={rejecting !== null}
        onOpenChange={(open) => !open && setRejecting(null)}
        title={t("invoicing.requests.rejectTitle")}
        description={t("invoicing.requests.rejectDescription")}
        confirmLabel={t("invoicing.requests.reject")}
        onConfirm={async (reason) => {
          if (!rejecting) return;
          await api.POST("/earchive-requests/{id}/reject", {
            params: { path: { id: rejecting.id } },
            body: { reason, version: rejecting.version },
          });
          await refresh(companyId);
          toast({ tone: "success", title: t("invoicing.requests.rejected") });
        }}
      />
    </>
  );
}
